/**
 * Role helpers for the Risk Management module.
 *
 * Mirrors the backend permission scheme documented in `permissions.py`:
 *
 * - **Admin / HR / Process Compliance Manager** = the "RMW Admin" set —
 *   write authority (enable, assign PM, templates, lock/unlock, cycle close).
 * - **Process Compliance Officer** = global read-only viewer.
 * - **Manager** = the assigned PM editor on their worksheets.
 * - **Employee** = project-team viewer.
 *
 * All comparisons are case-insensitive against the `selectedRole` string
 * stored in Redux.
 */

/** Roles allowed to enable RMW, assign PM, manage templates, lock cycles. */
export const RMW_ADMIN_ROLES = [
  "admin",
  "hr",
  "process compliance manager",
] as const;

/** Roles that can view org-wide dashboards (admin set + PCO). */
export const RMW_ORG_VIEWER_ROLES = [
  ...RMW_ADMIN_ROLES,
  "process compliance officer",
] as const;

export const isRmwAdmin = (role: string | undefined | null): boolean =>
  !!role && (RMW_ADMIN_ROLES as readonly string[]).includes(role.toLowerCase());

export const isRmwOrgViewer = (role: string | undefined | null): boolean =>
  !!role &&
  (RMW_ORG_VIEWER_ROLES as readonly string[]).includes(role.toLowerCase());

export const isRmwPM = (role: string | undefined | null): boolean =>
  role?.toLowerCase() === "manager";

export const isRmwEmployee = (role: string | undefined | null): boolean =>
  role?.toLowerCase() === "employee";

/** Landing route a user is redirected to if they hit a page they cannot view. */
export const rmwRoleHome = (role: string | undefined | null): string => {
  if (isRmwAdmin(role)) return "/pms/risk-management/dashboard";
  if (role?.toLowerCase() === "process compliance officer")
    return "/pms/risk-management/dashboard";
  if (isRmwPM(role)) return "/pms/risk-management/assignments";
  return "/landing";
};
